import {layoutText, type FontVariant, type TextLayout} from '../lvs-font/layout'
import {LINE_COEF, PADDING_COEF, table9_14, table9_15, table9_16} from './rules'

export type BgColor = 'white' | 'blue'

export interface FoniRow {
    readonly text: string
    /** Cap height in mm. */
    readonly h: number
    readonly variant: FontVariant
}

export interface BackgroundInput {
    readonly rows: readonly FoniRow[]
    readonly bgColor: BgColor
    /**
     * Override outer width (mm). If omitted, ≤ 0, or smaller than the
     * text-derived auto width, the auto value is used. Final outer width
     * is `max(outerWidth ?? autoW, autoW)`.
     */
    readonly outerWidth?: number
    /** See `outerWidth`. */
    readonly outerHeight?: number
    /**
     * Reserved space (mm) inside the inner box on the left side of the
     * row content, in addition to the standard 0.5 h margin. Used by
     * sign 705 (left arrow) to host the arrow + 0.5 h gap. Rows are
     * shifted right so they never enter this zone.
     */
    readonly innerPaddingLeft?: number
    /** See `innerPaddingLeft`. Mirror on the right (sign 705 right arrow). */
    readonly innerPaddingRight?: number
}

export interface RowPlacement {
    readonly index: number
    readonly row: FoniRow
    /** x of the row's left edge, inner-box-relative (mm). */
    readonly x: number
    /** y of the row's baseline, inner-box-relative (mm, +y down). */
    readonly baselineY: number
    readonly layout: TextLayout
}

export interface BackgroundLayout {
    /** Outer W/H/D in mm. */
    readonly width: number
    readonly height: number
    readonly diagonal: number
    readonly contrastBorder: number
    readonly blackEdge: number
    readonly cornerRadius: number
    readonly innerBox: {
        readonly x: number
        readonly y: number
        readonly width: number
        readonly height: number
    }
    readonly rowPlacements: readonly RowPlacement[]
    /**
     * Tallest row's cap height (mm), or 0 for empty input. Drives the
     * 0.5h padding zone and the 0.4h §9.20 strike width — both used by
     * SignBody, FoniCanvas, and FoniParamsTable.
     */
    readonly maxRowHeight: number
    /**
     * Number of border-lookup passes executed during fixed-point resolution.
     * Counts every iteration including the one that confirms stability, so
     * a single-step convergence reports `iterations: 2` (pass 1 changed the
     * borders, pass 2 saw no change and exited). Maximum is 5.
     * For empty input, `iterations` is 0.
     */
    readonly iterations: number
    readonly warnings: readonly Warning[]
}

export type Warning =
    | {
          readonly kind: 'diagonal-above-max-band'
          /** Outer diagonal in mm (same units as `BackgroundLayout.diagonal`). */
          readonly diagonal: number
      }
    | {readonly kind: 'empty-rows'}
    | {readonly kind: 'h-not-on-step'; readonly rowIndex: number; readonly h: number}
    | {readonly kind: 'h-out-of-spec'; readonly rowIndex: number; readonly h: number}
    | {readonly kind: 'non-convergent'; readonly iterations: number}

interface RowsLayout {
    readonly rowPlacements: readonly RowPlacement[]
    /** Width of the widest row (mm). */
    readonly contentWidth: number
    /** Total content height: baseline of last row (mm). */
    readonly contentHeight: number
}

/**
 * Stacks rows vertically. Row 0's top is at y=0; its baseline is at y=row[0].h.
 * Each subsequent row's baseline sits `(11/7) × max(h_prev, h_curr)` below the
 * previous baseline. Row left edges are all at x=0.
 */
export function layoutRows(rows: readonly FoniRow[]): RowsLayout {
    if (rows.length === 0) {
        return {rowPlacements: [], contentWidth: 0, contentHeight: 0}
    }

    const placements: RowPlacement[] = []
    let maxWidth = 0
    let baselineY = rows[0].h

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        if (i > 0) {
            const prev = rows[i - 1]
            baselineY += LINE_COEF * Math.max(prev.h, row.h)
        }
        const textLayout = layoutText(row.text, row.variant, row.h)
        placements.push({index: i, row, x: 0, baselineY, layout: textLayout})
        if (textLayout.totalWidth > maxWidth) maxWidth = textLayout.totalWidth
    }

    return {
        rowPlacements: placements,
        contentWidth: maxWidth,
        contentHeight: baselineY
    }
}

const MAX_ITERATIONS = 5
const WHITE_MAX_DIAG_M = 6.25
const BLUE_MAX_DIAG_M = 7.3
const H_MIN = 100
const H_MAX = 400
const H_STEP = 50

function lookupBorders(
    diagM: number,
    bgColor: BgColor
): {contrastBorder: number; blackEdge: number} {
    if (bgColor === 'blue') {
        return {contrastBorder: table9_14(diagM), blackEdge: 0}
    }
    return table9_15(diagM)
}

/**
 * Per-row h validation. At most one warning is emitted per row — an
 * out-of-spec h (outside 100–400 mm) takes precedence over a not-on-step
 * value, since a row can only fail one check at a time.
 */
function hWarnings(rows: readonly FoniRow[]): Warning[] {
    const ws: Warning[] = []
    for (let i = 0; i < rows.length; i++) {
        const h = rows[i].h
        if (h < H_MIN || h > H_MAX) {
            ws.push({kind: 'h-out-of-spec', rowIndex: i, h})
        } else if (h % H_STEP !== 0) {
            ws.push({kind: 'h-not-on-step', rowIndex: i, h})
        }
    }
    return ws
}

export function computeBackground(input: BackgroundInput): BackgroundLayout {
    const {rows, bgColor, outerWidth, outerHeight} = input
    const paddingL = input.innerPaddingLeft ?? 0
    const paddingR = input.innerPaddingRight ?? 0

    if (rows.length === 0) {
        const contrastBorder = 10
        const blackEdge = bgColor === 'white' ? 10 : 0
        const offset = contrastBorder + blackEdge
        return {
            width: 2 * offset,
            height: 2 * offset,
            diagonal: Math.sqrt(2) * 2 * offset,
            contrastBorder,
            blackEdge,
            cornerRadius: 45,
            innerBox: {x: offset, y: offset, width: 0, height: 0},
            rowPlacements: [],
            maxRowHeight: 0,
            iterations: 0,
            warnings: [{kind: 'empty-rows'}]
        }
    }

    const rowsLayout = layoutRows(rows)
    const hMax = Math.max(...rows.map(r => r.h))
    const margin = PADDING_COEF * hMax
    const innerW = rowsLayout.contentWidth + 2 * margin + paddingL + paddingR
    const innerH = rowsLayout.contentHeight + 2 * margin

    // Optional caller-supplied outer floor. Final outer is always at least
    // the text-derived auto value, so a too-small override silently falls
    // back to auto.
    const userMinW = outerWidth !== undefined && outerWidth > 0 ? outerWidth : 0
    const userMinH = outerHeight !== undefined && outerHeight > 0 ? outerHeight : 0

    let contrastBorder = 10
    let blackEdge = bgColor === 'white' ? 10 : 0
    let W = 0
    let H = 0
    let D = 0
    let iterations = 0

    // Single fixed-point loop: outer = max(content+borders, user-floor).
    // When borders grow they push the auto outer up, which can in turn
    // grow the diagonal into a new band. We re-resolve until both the
    // borders and the outer dimensions stabilize, capped at MAX_ITERATIONS.
    let converged = false
    for (let i = 1; i <= MAX_ITERATIONS; i++) {
        const offset = contrastBorder + blackEdge
        const autoW = innerW + 2 * offset
        const autoH = innerH + 2 * offset
        W = Math.max(autoW, userMinW)
        H = Math.max(autoH, userMinH)
        D = Math.sqrt(W * W + H * H)
        const next = lookupBorders(D / 1000, bgColor)
        iterations = i
        if (next.contrastBorder === contrastBorder && next.blackEdge === blackEdge) {
            converged = true
            break
        }
        contrastBorder = next.contrastBorder
        blackEdge = next.blackEdge
    }

    const cornerRadius = table9_16(D / 1000)
    const offset = contrastBorder + blackEdge
    const finalInnerW = W - 2 * offset
    const finalInnerH = H - 2 * offset
    const innerBox = {x: offset, y: offset, width: finalInnerW, height: finalInnerH}

    // Shift row placements from content-frame to inner-box frame.
    // layoutRows emits rowPlacements in content-frame (row 0 top at y=0).
    // Inner-box frame inserts the margin padding on every side, plus any
    // extra slack from a user-requested outer size (centred on both axes),
    // and any reserved arrow zone (paddingL/paddingR). Rows are centered
    // in the *available* horizontal space between the paddings — the arrow
    // zone is exclusive and rows never enter it.
    // When paddings are 0 and no override is in effect, slackX = slackY =
    // margin (identical to the pre-padding behaviour).
    const availableW = finalInnerW - paddingL - paddingR
    const slackX = paddingL + (availableW - rowsLayout.contentWidth) / 2
    const slackY = (finalInnerH - rowsLayout.contentHeight) / 2
    const rowPlacements: RowPlacement[] = rowsLayout.rowPlacements.map(p => ({
        ...p,
        x: slackX,
        baselineY: slackY + p.baselineY
    }))

    const warnings: Warning[] = [...hWarnings(rows)]
    if (!converged) {
        warnings.push({kind: 'non-convergent', iterations})
    }
    const maxBandM = bgColor === 'white' ? WHITE_MAX_DIAG_M : BLUE_MAX_DIAG_M
    if (D / 1000 > maxBandM) {
        warnings.push({kind: 'diagonal-above-max-band', diagonal: D})
    }

    return {
        width: W,
        height: H,
        diagonal: D,
        contrastBorder,
        blackEdge,
        cornerRadius,
        innerBox,
        rowPlacements,
        maxRowHeight: hMax,
        iterations,
        warnings
    }
}
